/** Where the API lives, and the rule for deciding — **ADR-0049, option D1.**
 *
 * In production the SPA is `https://app.<domain>` and the API is `https://api.<domain>`: same
 * *site*, different *origin*. That one fact needs three agreeing settings, and this module owns the
 * first and the checkable half of the second:
 *
 * 1. a **base URL** for every request (`resolveApiOrigin` → `API_ORIGIN` in `client.ts`);
 * 2. **`credentials: "include"`**, because `fetch` withholds cookies cross-origin by default even
 *    when `SameSite=Lax` would allow them (`client.ts`);
 * 3. a **`connect-src`** in `index.html` that names that origin, or the browser blocks the request
 *    before CORS is consulted (`assertPolicyAdmitsApiOrigin`, run by the build).
 *
 * **This file imports nothing from the app and reads no environment**, so `vite.config.ts` can
 * import it (the build checks the policy against the value the bundle will embed) and a test can
 * call it with any input. `client.ts` is the one place that reads `import.meta.env`.
 */

/** The build-time variable. One name, so the client, the build guard and the deploy all say it. */
export const API_ORIGIN_VARIABLE = "VITE_API_ORIGIN";

/**
 * The origin every API request goes to: `https://api.example.com`, or `""` for **same-origin**.
 *
 * Three inputs, three outcomes, and the distinction between the first two is the whole design:
 *
 * - **unset** (`undefined`) in a production build → **throws.** Nobody said where the API is.
 * - **set to the empty string** → same-origin, *deliberately*. This is how `vite preview` behind its
 *   proxy (`make ui-check` in CI) and a same-origin topology (ADR-0049's option A) say so.
 * - **unset** outside production → same-origin, because `vite dev` proxies `/api` and there is
 *   nothing to configure on a laptop.
 *
 * **Why a production build refuses rather than falling back to relative.** A relative fallback
 * would not fail; it would call `app.<domain>/api/…`, which is CloudFront serving static files.
 * A static host answers an unknown path with the SPA's own `index.html` and a 200, so every read
 * would "succeed" with HTML, the sign-in would never set a cookie anywhere, and the symptom — an
 * app that loads and shows nothing — is indistinguishable from a broken API. `ADR-0049` names the
 * same shape in its own words: a silent default that is wrong in the one environment nobody
 * develops in. `backend/app/core/config.py`'s `assert_no_development_defaults` is the same refusal
 * on the other side: a deployment that omits a setting stops rather than running on a development
 * value. A failure at build (or, if built some other way, at load) names the variable; a fallback
 * names nothing.
 *
 * **A non-empty value must be a bare origin**, because it is concatenated in front of every path
 * and spliced into a security policy: no path, no trailing slash, no credentials, no query, no
 * wildcard. `https` only in production; plain `http` is allowed elsewhere so a developer can point
 * the dev server at a locally run API on another port.
 */
export function resolveApiOrigin(raw: string | undefined, production: boolean): string {
  if (raw === undefined) {
    if (production) {
      throw new Error(
        `${API_ORIGIN_VARIABLE} is not set in a production build. Set it to the API's origin ` +
          "(for example https://api.example.com), or to the empty string if this build is " +
          "deliberately served from the same origin as the API. It is never guessed, because a " +
          "guess here is a static host answering every API call with the app's own HTML.",
      );
    }
    return "";
  }
  if (raw === "") return "";

  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    throw new Error(`${API_ORIGIN_VARIABLE} is not a URL: ${JSON.stringify(raw)}.`);
  }
  // `URL.origin` drops the path, query, fragment and credentials, so equality with the input is
  // the test for "this was already nothing but an origin" — including the trailing slash.
  if (parsed.origin === "null" || parsed.origin !== raw) {
    throw new Error(
      `${API_ORIGIN_VARIABLE} must be a bare origin (scheme, host and optional port; no path, ` +
        `trailing slash, credentials or query). Got ${JSON.stringify(raw)}.`,
    );
  }
  // `new URL` accepts a `*` in a host and `URL.origin` hands it back unchanged, so equality above
  // does not stop `https://*.example.com` — which would be a wildcard source in the policy.
  if (!/^[a-z0-9]([a-z0-9.-]*[a-z0-9])?$/.test(parsed.hostname)) {
    throw new Error(
      `${API_ORIGIN_VARIABLE} must name a single DNS host (no wildcard). Got ${JSON.stringify(raw)}.`,
    );
  }
  if (production && parsed.protocol !== "https:") {
    throw new Error(
      `${API_ORIGIN_VARIABLE} must be https in a production build. Got ${JSON.stringify(raw)}.`,
    );
  }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
    throw new Error(`${API_ORIGIN_VARIABLE} must be http(s). Got ${JSON.stringify(raw)}.`);
  }
  return raw;
}

/** `origin` + a root-relative API path. `""` leaves the path relative, which the dev proxy serves.
 *
 * **`//host/…` is refused**: with an empty origin it is a *protocol-relative URL*, which the browser
 * reads as another host entirely, and nothing here should be able to produce one by accident. */
export function joinApiUrl(origin: string, path: string): string {
  if (!path.startsWith("/") || path.startsWith("//")) {
    throw new Error(`An API path starts with a single "/". Got ${JSON.stringify(path)}.`);
  }
  return `${origin}${path}`;
}

const CSP_META = /<meta\s+http-equiv="Content-Security-Policy"\s+content="([^"]+)"/;

/** The tokens of one directive in the CSP `<meta>` tag of `html`, or a failure naming what is missing. */
export function directiveTokens(html: string, directive: string): string[] {
  const found = CSP_META.exec(html);
  if (!found || found[1] === undefined) {
    throw new Error("index.html carries no Content-Security-Policy meta tag this check can read.");
  }
  for (const part of found[1].split(";")) {
    const tokens = part.trim().split(/\s+/);
    if (tokens[0] === directive) return tokens.slice(1);
  }
  throw new Error(`index.html's Content-Security-Policy has no ${directive} directive.`);
}

/**
 * The one other host `connect-src` may name besides the API: the landing bucket a browser uploads a
 * document straight into (**ADR-0058**, option A2) - `https://<bucket>.s3.<region>.amazonaws.com`.
 *
 * Deliberately a *shape*, not a literal: the bucket's name is `foundation`'s variable, and
 * `test_security_headers.py` is what pins the literal in `index.html` to Terraform's declaration.
 * What this guards is that nothing wider can stand in its place, so each part is the narrow form:
 * a single bucket label with no dot and no wildcard (a dot would change the wildcard certificate
 * the endpoint is served under, and a `*` would admit every bucket in the region, an attacker's
 * included), the `s3` label, a **regional** name (never the global `s3.amazonaws.com`, which is a
 * residency question), and no path. The path-style `https://s3.<region>.amazonaws.com` fails
 * because it has no bucket label before `.s3.`.
 */
const LANDING_ORIGIN = /^https:\/\/[a-z0-9][a-z0-9-]{1,61}[a-z0-9]\.s3\.[a-z]{2}(?:-[a-z]+)+-[0-9]\.amazonaws\.com$/;

/** Is `origin` a landing-bucket origin of the shape above? Exported so tests apply the same rule. */
export function isLandingOrigin(origin: string): boolean {
  return LANDING_ORIGIN.test(origin);
}

/**
 * Fail unless the policy lets the page reach `origin` - the third setting in the list at the top
 * of this file, and the one a browser enforces before CORS is even consulted - **and nothing wider**.
 *
 * `connect-src` must be `'self'`, that origin, and optionally **one** landing-bucket origin
 * (`isLandingOrigin`). Missing the API origin blocks every API call. Any other host is the widening
 * ADR-0049 forbids (*"by exactly one host"*) and the vite config's own comment says each new host
 * is named one at a time: ADR-0058 added exactly one, and a second landing-shaped host, a wildcard
 * or a bare scheme is still refused. The landing host is *optional* here because this guard's job is
 * the API origin; whether the landing host is present, and is the right bucket, is pinned from the
 * Terraform side by `test_security_headers.py`. An empty `origin` is a same-origin build and has
 * nothing to name, so it is not checked - the policy then admits hosts the build never calls, which
 * is wider than needed and not broken, and refusing it would make `vite preview` (the production
 * policy, served behind a proxy) impossible to gate on.
 */
export function assertPolicyAdmitsApiOrigin(html: string, origin: string): void {
  if (origin === "") return;
  const tokens = directiveTokens(html, "connect-src");
  const hosts = tokens.filter((token) => token !== "'self'");
  const others = hosts.filter((host) => host !== origin);
  const admitted =
    tokens.includes("'self'") &&
    hosts.includes(origin) &&
    hosts.length === new Set(hosts).size &&
    others.length <= 1 &&
    others.every(isLandingOrigin);
  if (!admitted) {
    throw new Error(
      `${API_ORIGIN_VARIABLE} is ${origin} but index.html's connect-src is ` +
        `${JSON.stringify(tokens.join(" "))}. It must be 'self', that origin, and at most one ` +
        "landing-bucket origin (https://<bucket>.s3.<region>.amazonaws.com, ADR-0058): without " +
        "the API origin the browser blocks every API call, and anything more is a wider policy " +
        "than ADR-0049 and ADR-0058 allow.",
    );
  }
}
